import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '@chakra-ui/react'
import { asChakraVariable, themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

import { phoneColorKeys } from './_colors'

const { CallContactButtonMenu: menuColors, CallContactButton: buttonColors } =
  phoneColorKeys.Phone

const dropdownShadow = themeColorSectionKeys.shared.Dropdown.BoxShadow

export enum CallContactButtonMenuVariants {
  Fixed = 'fixed',
  Scrollable = 'scrollable',
}

const CallContactButtonMenuParts = [
  'container',
  'itemContainer',
  'itemText',
  'itemBadge',
] as const

const menuHelpers = createMultiStyleConfigHelpers(CallContactButtonMenuParts)
export const CallContactButtonMenu = menuHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      flexDirection: 'column',
      padding: '0.25rem',
      borderRadius: '0.38rem',
      gap: '0.5rem',
      backgroundColor: menuColors.Container.Background,
      boxShadow: `0px 4px 12px 0px ${asChakraVariable(dropdownShadow)}`,
    },
    itemContainer: {
      alignItems: 'center',
      padding: '0.5rem',
      gap: '0.5rem',
      _hover: { backgroundColor: menuColors.ItemContainer.Hover.Background },
      cursor: 'pointer',
    },
    itemText: {
      textStyle: textStyleKeys.Body1,
    },
    itemBadge: {
      backgroundColor: menuColors.ItemBadge.Background,
      color: menuColors.ItemBadge.FontColor,
      textStyle: textStyleKeys.Body4_Bold,
    },
  },
  variants: {
    [CallContactButtonMenuVariants.Fixed]: {},
    [CallContactButtonMenuVariants.Scrollable]: {
      container: {
        // We want to show 5 (to match max fixed) and scroll for more
        // 15rem was a lucky guess
        maxHeight: '15rem',
        overflowY: 'scroll',
      },
    },
  },
  defaultProps: {
    variant: CallContactButtonMenuVariants.Fixed,
  },
})

export const useCallContactButtonMenuStyles = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig<typeof CallContactButtonMenuParts>(
    getName({ CallContactButtonMenu }),
    props,
  )
}

export enum CallContactButtonVariants {
  Visible = 'visible',
  Hidden = 'hidden',
}

const CallContactButtonParts = [
  'container',
  'callButton',
  'callButtonIcon',
  'callButtonText',
  'menuButton',
  'menuButtonIconContainer',
  'menuButtonIcon',
] as const

const buttonHelpers = createMultiStyleConfigHelpers(CallContactButtonParts)
export const CallContactButton = buttonHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      flexShrink: 0,
      gap: '0.06rem',
      borderRadius: '3.125rem',
      '& > button:first-of-type': {
        borderRadius: '3.125rem 0rem 0rem 3.125rem',
      },
      '& > button:last-of-type': {
        borderRadius: '0rem 3.125rem 3.125rem 0rem',
      },
      '& > button:only-of-type': { borderRadius: '3.125rem' },
      '&:hover': {
        boxShadow: `0px 4px 12px 0px ${asChakraVariable(
          buttonColors.Button.Hover.BoxShadow,
        )}`,
      },
    },
    callButton: {
      justifyContent: 'center',
      alignItems: 'center',
      gap: '0.38rem',
      backgroundColor: buttonColors.Button.Background,
      paddingY: '0.5rem',
      paddingX: '0.75rem',
      cursor: 'pointer',
      _hover: {
        backgroundColor: buttonColors.Button.Hover.Background,
      },
      _active: {
        backgroundColor: buttonColors.Button.Active.Background,
        boxShadow: `0px 4px 12px 0px ${asChakraVariable(
          buttonColors.Button.Active.BoxShadow,
        )}`,
      },
    },
    callButtonIcon: {
      boxSize: '1rem',
      color: buttonColors.ButtonIcon.FontColor,
    },
    callButtonText: {
      textStyle: textStyleKeys.Body2_Semibold,
      color: buttonColors.ButtonText.FontColor,
    },
    menuButton: {
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: buttonColors.Button.Background,
      cursor: 'pointer',
      _hover: {
        backgroundColor: buttonColors.Button.Hover.Background,
      },
      _active: {
        backgroundColor: buttonColors.Button.Active.Background,
        boxShadow: `0px 4px 12px 0px ${asChakraVariable(
          buttonColors.Button.Active.BoxShadow,
        )}`,
      },
    },
    menuButtonIconContainer: {
      boxSize: 'full',
      alignItems: 'center',
      justifyContent: 'center',
      paddingY: '0.5rem',
      paddingRight: '0.75rem',
      paddingLeft: '0.5rem',
    },
    menuButtonIcon: {
      boxSize: '0.75rem',
      color: buttonColors.ButtonIcon.FontColor,
    },
  },
  variants: {
    [CallContactButtonVariants.Visible]: {
      container: { display: 'flex' },
    },
    [CallContactButtonVariants.Hidden]: {
      container: { display: 'none' },
    },
  },
  defaultProps: {
    variant: CallContactButtonVariants.Visible,
  },
})

export const useCallContactButtonStyles = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig<typeof CallContactButtonParts>(
    getName({ CallContactButton }),
    props,
  )
}
